import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle, FileCheck } from 'lucide-react';
import api from '@/utils/api';
import { getUser, setAuth } from '@/utils/auth';
import { toast } from 'sonner';

const TERMS = [
  {
    id: 'recruiter',
    text: 'I am an authorized recruiter of the above-mentioned organization.'
  },
  {
    id: 'no_charges',
    text: 'I do not collect any fees from job seekers.'
  },
  {
    id: 'not_consultant',
    text: 'I am not a consultant or freelance recruiter; I represent a company HR or an AICTE-approved NEEM / NAPS / Apprentice Partner.'
  },
  {
    id: 'legal_action',
    text: 'I acknowledge that VVVSI provides employment services as a social cause, and any collection of money from candidates will attract legal action by VVVSI.'
  },
  {
    id: 'job_post_decision',
    text: "I accept that job postings are subject to VVVSI's internal policies and approval."
  }
];

const EmployerTermsModal = ({ open, onAccept }) => {
  const user = getUser();
  const [checkedTerms, setCheckedTerms] = useState({});
  const [loading, setLoading] = useState(false);
  const [allChecked, setAllChecked] = useState(false);

  useEffect(() => {
    // Check if all terms are checked
    const allTermsChecked = TERMS.every(term => checkedTerms[term.id]);
    setAllChecked(allTermsChecked);
  }, [checkedTerms]);

  const handleCheckChange = (termId, checked) => {
    setCheckedTerms(prev => ({
      ...prev,
      [termId]: checked
    }));
  };

  const handleAccept = async () => {
    if (!allChecked) {
      toast.error('Please agree to all terms to proceed');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/auth/accept-terms', { user_id: user.id });
      
      // Update local user data
      const updatedUser = {
        ...user,
        terms_accepted: true,
        terms_accepted_at: response.data.accepted_at
      };
      setAuth(localStorage.getItem('auth_token'), updatedUser);
      
      toast.success('Terms accepted successfully');
      onAccept();
    } catch (error) {
      toast.error('Failed to accept terms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            Important Terms & Conditions
          </DialogTitle>
          <DialogDescription>
            Please read and agree to all terms before proceeding to the dashboard
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium">
              Agree Terms For Employer:
            </p>
            <p className="text-xs text-amber-700 mt-1">
              You must agree to all the following terms to access your employer dashboard.
            </p>
          </div>

          <div className="space-y-4">
            {TERMS.map((term, index) => (
              <div 
                key={term.id} 
                className={`p-4 rounded-lg border transition-colors ${
                  checkedTerms[term.id] 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    id={term.id}
                    checked={checkedTerms[term.id] || false}
                    onCheckedChange={(checked) => handleCheckChange(term.id, checked)}
                    className="mt-0.5"
                    data-testid={`term-checkbox-${term.id}`}
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-slate-900">
                      {index + 1}. {term.text}
                    </span>
                  </div>
                </label>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-slate-200">
            <Button
              onClick={handleAccept}
              disabled={!allChecked || loading}
              className={`w-full h-12 text-lg ${
                allChecked 
                  ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                  : 'bg-slate-300 text-slate-500 cursor-not-allowed'
              }`}
              data-testid="accept-terms-btn"
            >
              {loading ? (
                'Processing...'
              ) : (
                <>
                  <FileCheck className="w-5 h-5 mr-2" />
                  I Agree to All Terms & Continue
                </>
              )}
            </Button>
            
            {!allChecked && (
              <p className="text-xs text-center text-slate-500 mt-2">
                Please check all the boxes above to proceed
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmployerTermsModal;
